import { Contract, ContractHandover, ContractHandoverCms, getContractHandoverEndpoint } from 'cpnl-common';
import {
    CleaveInput,
    preventSubmit,
    ValidatedCheckbox,
    ValidatedInput,
    ValidatedSelectItem,
} from '@cp-shared-19/frontend-ui';
import {
    Button,
    ButtonContainer,
    DataOverview,
    Fieldset,
    Form,
    Layout,
    RadioButton,
    FormFieldRadioButtonGroup,
    Select,
    FormField,
} from '@vwfs-bronson/bronson-react';
import { CpDataApi } from 'cp-xhr';
import { Formik } from 'formik';
import React, { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { ContractHandoverModal } from './ContractHandoverModal';
import { validationSchema } from './validationSchema';
import { countryCodeMapping } from '../../../../my-profile/contact-section/edit-view/country-code';
import { UiBlockingSpinner } from '../../../../spinner';

const optionsFromCountryMapping = (): ValidatedSelectItem[] => {
    return Object.entries(countryCodeMapping)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map((value: [string, string[]]) => ({
            label: value[0],
            value: value[1][0],
        }));
};

export type ContractHandoverFormProps = {
    contract?: Contract;
    cmsContent?: ContractHandoverCms;
    onCancel: () => void;
};

export const ContractHandoverForm: React.FC<ContractHandoverFormProps> = ({ onCancel, cmsContent, contract }) => {
    const { t } = useTranslation('contract-handover');
    const history = useHistory();
    const { path } = useRouteMatch();

    const initialValues = {
        companyName: '',
        companyID: '',
        gender: 'Dhr.',
        initials: '',
        firstName: '',
        middleName: '',
        lastName: '',
        email: '',
        mobileNumberCountryCode: '31',
        mobileNumber: '',
        consentFlag: false,
    };

    const [prefixMobilePhone, setPrefixMobilePhone] = useState(initialValues?.mobileNumberCountryCode);
    const [showSuccessDialogue, setShowSuccessDialogue] = useState(false);
    const [showErrorDialogue, setShowErrorDialogue] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handlerChangeInitialsInput = (
        e: ChangeEvent<HTMLInputElement>,
        setFieldValue: (name: string, value: string) => void,
    ): void => {
        const regex = /^([A-Z]\.)+$/;

        if (!regex.test(e.target.value)) {
            setFieldValue('initials', e.target.value.slice(0, -2));
        }
    };

    const handlerChangeAreaCodeMobilePhone = (
        event: ChangeEvent<HTMLInputElement>,
        setFieldValue: (name: string, value: string) => void,
    ): void => {
        setFieldValue('mobileNumberCountryCode', event.target.value);
        setPrefixMobilePhone(event.target.value);
    };

    const handleSubmit = (values: ContractHandover): void => {
        setIsSubmitting(true);
        CpDataApi.post(getContractHandoverEndpoint(contract?.encryptedContractVersionId || ''), values)
            .then(() => {
                setIsSubmitting(false);
                setShowSuccessDialogue(true);
            })
            .catch(() => {
                setShowErrorDialogue(true);
            });
    };

    const handleModalCancel = (): void => {
        history.push(path);
        setShowErrorDialogue(false);
    };

    const isProductTypeFL = () => contract?.productType?.includes('Financial Lease');

    if (!cmsContent || !contract) return null;

    return (
        <DataOverview title={cmsContent?.subheadline}>
            <UiBlockingSpinner isBlocking={isSubmitting}>
                <Formik
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validationSchema={validationSchema(t, prefixMobilePhone, contract?.productType || '')}
                >
                    {(formik) => (
                        <>
                            <Form onSubmit={preventSubmit}>
                                <Fieldset>
                                    {isProductTypeFL() && (
                                        <Fieldset.Row>
                                            <Layout>
                                                <Layout.Item default="1/3">
                                                    <ValidatedInput
                                                        className="c-form-field--no-floating"
                                                        label={cmsContent?.companyName}
                                                        name="companyName"
                                                        testId="companyName"
                                                        type="text"
                                                        required
                                                        autocomplete="organization"
                                                    />
                                                </Layout.Item>
                                                <Layout.Item default="1/3">
                                                    <ValidatedInput
                                                        className="c-form-field--no-floating"
                                                        label={cmsContent?.companyID}
                                                        name="companyID"
                                                        testId="companyID"
                                                        type="text"
                                                        required
                                                        autocomplete="on"
                                                    />
                                                </Layout.Item>
                                            </Layout>
                                        </Fieldset.Row>
                                    )}
                                    <Fieldset.Row>
                                        <Layout>
                                            <Layout.Item>
                                                <p>{cmsContent?.gender}</p>
                                                <FormFieldRadioButtonGroup>
                                                    <RadioButton
                                                        required
                                                        name="gender"
                                                        value="Dhr."
                                                        testId="maleGender"
                                                        defaultChecked={true}
                                                        onChange={() => formik.setFieldValue('gender', 'Dhr.')}
                                                    >
                                                        {cmsContent?.male}
                                                    </RadioButton>
                                                    <RadioButton
                                                        required
                                                        name="gender"
                                                        value="Mw."
                                                        testId="femaleGender"
                                                        onChange={() => formik.setFieldValue('gender', 'Mw.')}
                                                    >
                                                        {cmsContent?.female}
                                                    </RadioButton>
                                                </FormFieldRadioButtonGroup>
                                            </Layout.Item>
                                            <Layout.Item default="1/3">
                                                <CleaveInput
                                                    className="c-form-field--no-floating"
                                                    label={cmsContent?.initials || ''}
                                                    name="initials"
                                                    testId="initials"
                                                    type="text"
                                                    required
                                                    autocomplete="on"
                                                    cleaveOptions={{
                                                        delimiter: '.',
                                                        blocks: [1, 1, 1, 1, 1, 1],
                                                        numericOnly: false,
                                                        uppercase: true,
                                                    }}
                                                    handleChange={(e: unknown) =>
                                                        handlerChangeInitialsInput(
                                                            e as ChangeEvent<HTMLInputElement>,
                                                            formik.setFieldValue,
                                                        )
                                                    }
                                                />
                                            </Layout.Item>
                                        </Layout>
                                    </Fieldset.Row>
                                    <Fieldset.Row>
                                        <Layout>
                                            <Layout.Item default="1/3">
                                                <ValidatedInput
                                                    className="c-form-field--no-floating"
                                                    label={cmsContent?.firstName || ''}
                                                    name="firstName"
                                                    testId="firstName"
                                                    required
                                                    type="text"
                                                    autocomplete="given-name"
                                                />
                                            </Layout.Item>
                                            <Layout.Item default="1/3">
                                                <ValidatedInput
                                                    className="c-form-field--no-floating"
                                                    label={cmsContent?.middleName || ''}
                                                    name="middleName"
                                                    testId="middleName"
                                                    type="text"
                                                    autocomplete="additional-name"
                                                />
                                            </Layout.Item>
                                            <Layout.Item default="1/3">
                                                <ValidatedInput
                                                    className="c-form-field--no-floating"
                                                    label={cmsContent?.lastName || ''}
                                                    name="lastName"
                                                    required
                                                    testId="lastName"
                                                    type="text"
                                                    autocomplete="family-name"
                                                />
                                            </Layout.Item>
                                        </Layout>
                                    </Fieldset.Row>
                                    <Fieldset.Row>
                                        <Layout>
                                            <Layout.Item default="1/3">
                                                <ValidatedInput
                                                    className="c-form-field--no-floating"
                                                    label={cmsContent?.email || ''}
                                                    name="email"
                                                    testId="email"
                                                    type="email"
                                                    required
                                                    autocomplete="email"
                                                />
                                            </Layout.Item>
                                            <Layout.Item default="1/3">
                                                <FormField
                                                    type="select"
                                                    className="c-form-field--no-floating"
                                                    labelText={cmsContent?.mobileNumberCountryCode || ''}
                                                    testid="mobileNumberCountryCodeFormField"
                                                    required
                                                    name="mobileNumberCountryCode"
                                                >
                                                    <Select
                                                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                            handlerChangeAreaCodeMobilePhone(e, formik.setFieldValue)
                                                        }
                                                        id={prefixMobilePhone}
                                                        defaultValue={prefixMobilePhone}
                                                        required
                                                        testid="mobileNumberCountryCode"
                                                    >
                                                        {optionsFromCountryMapping().map((item, index) => {
                                                            return (
                                                                <Select.Item key={index} value={item.value}>
                                                                    {item.label}
                                                                </Select.Item>
                                                            );
                                                        })}
                                                    </Select>
                                                </FormField>
                                            </Layout.Item>
                                            <Layout.Item default="1/3">
                                                <ValidatedInput
                                                    className="c-form-field--no-floating"
                                                    label={cmsContent?.mobileNumber || ''}
                                                    name="mobileNumber"
                                                    testId="mobileNumber"
                                                    type="tel"
                                                    required
                                                    addonText={`+${prefixMobilePhone}`}
                                                    reversed
                                                    autocomplete="tel"
                                                />
                                            </Layout.Item>
                                        </Layout>
                                    </Fieldset.Row>
                                    <Fieldset.Row>
                                        <Layout>
                                            <Layout.Item>
                                                <ValidatedCheckbox
                                                    label={cmsContent?.consentFlag}
                                                    name="consentFlag"
                                                    testId="consentFlag"
                                                    isMandatory={true}
                                                />
                                            </Layout.Item>
                                        </Layout>
                                    </Fieldset.Row>
                                    <Fieldset.Row>
                                        <ButtonContainer center>
                                            <Button
                                                secondary
                                                onClick={onCancel}
                                                type="button"
                                                testId="cancelButton"
                                                ariaLabel={cmsContent?.backButton.ariaLabel}
                                                title={cmsContent?.backButton.title}
                                            >
                                                {cmsContent?.backButton.label}
                                            </Button>
                                            <Button
                                                onClick={formik.handleSubmit}
                                                type="submit"
                                                testId="submitButton"
                                                ariaLabel={cmsContent?.submitButton.ariaLabel}
                                                title={cmsContent?.submitButton.title}
                                            >
                                                {cmsContent?.submitButton.label}
                                            </Button>
                                        </ButtonContainer>
                                    </Fieldset.Row>
                                </Fieldset>
                            </Form>
                            <ContractHandoverModal
                                showModal={showSuccessDialogue || showErrorDialogue}
                                status={showSuccessDialogue ? 'success' : 'error'}
                                closeDialog={showSuccessDialogue ? onCancel : handleModalCancel}
                                cmsContent={cmsContent}
                            />
                        </>
                    )}
                </Formik>
            </UiBlockingSpinner>
        </DataOverview>
    );
};
